// src/pages/TasksPhotosTab.tsx
// ─────────────────────────────────────────────────────────────────
// TASKS & PHOTOS TAB — Phase 3 implementation
// Job filter, task cards with Go/No-Go, AI task approval,
// photo review, deliveries GRN form (wired to mockDataGRN)
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from 'react'
import {
  Camera, CheckCircle, Flag, RefreshCw, Plus, Download,
  Package, Brain, FileText, MapPin, Clock, X, Send,
  AlertTriangle, ChevronDown, ChevronUp, Truck, Eye,
} from 'lucide-react'
import {
  getAllAssignableTasks, getTaskSubmissions, getPhotoSubmissions,
  approvePhoto, rejectPhoto, calculateGoNoGo,
  approveAITask, rejectAITask, assignTask,
} from '../services/taskService'
import type { AssignableTask, GoNoGoStatus } from '../types/mobileCrewTypes'
import type { MobileTaskRecord, MobilePhoto } from '../utils/mockDataMobile'
import { addDeliveryConfirmation } from '../utils/mockDataGRN'
import { MOCK_EMPLOYEES } from '../utils/mockDataExtended'
import { MOCK_JOBS } from '../utils/mockData'

// ── Go/No-Go badge ─────────────────────────────────────────────────
function GoNoBadge({ status }: { status: GoNoGoStatus }) {
  const m = {
    GO:             { bg:'bg-green-100',  text:'text-green-800',  icon:'✓', label:'GO'       },
    CONDITIONAL_GO: { bg:'bg-amber-100',  text:'text-amber-800',  icon:'⚡', label:'COND. GO' },
    NO_GO:          { bg:'bg-red-100',    text:'text-red-800',    icon:'✕', label:'NO GO'    },
    NO_MATERIALS:   { bg:'bg-gray-100',   text:'text-gray-500',   icon:'–', label:'No matls' },
  }[status] ?? { bg:'bg-gray-100', text:'text-gray-500', icon:'', label:status }
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${m.bg} ${m.text}`}>
      {m.icon} {m.label}
    </span>
  )
}

// ── Task card ──────────────────────────────────────────────────────
const TASK_STATUS_META: Record<string, { bg:string; text:string; label:string }> = {
  NOT_STARTED:           { bg:'bg-gray-100',   text:'text-gray-600',   label:'Not started'      },
  IN_PROGRESS:           { bg:'bg-blue-100',   text:'text-blue-700',   label:'In progress'       },
  COMPLETE_PENDING_REVIEW:{ bg:'bg-amber-100', text:'text-amber-700',  label:'Pending review'    },
  ACCEPTED:              { bg:'bg-green-100',  text:'text-green-700',  label:'Accepted'           },
  REQUIRES_REWORK:       { bg:'bg-red-100',    text:'text-red-700',    label:'Rework required'   },
}

function TaskCard({
  task, submission, currentUser, onAccept, onRework,
}: {
  task:        AssignableTask
  submission:  MobileTaskRecord | null
  currentUser: any
  onAccept:    (id: string) => void
  onRework:    (id: string, note: string) => void
}) {
  const [expanded,   setExpanded]   = useState(false)
  const [reworkNote, setReworkNote] = useState('')
  const [showRework, setShowRework] = useState(false)

  const status = submission?.status ?? 'NOT_STARTED'
  const meta   = TASK_STATUS_META[status] ?? TASK_STATUS_META.NOT_STARTED
  const gng    = calculateGoNoGo(task.materialsRequired)

  return (
    <div className={`bg-white border rounded-xl overflow-hidden transition-all ${
      status === 'COMPLETE_PENDING_REVIEW' ? 'border-amber-200' :
      status === 'REQUIRES_REWORK'         ? 'border-red-200'   :
      status === 'ACCEPTED'                ? 'border-green-200' : 'border-gray-200'
    }`}>
      <div className="flex items-start gap-3 px-4 py-3">
        {/* Task source icon */}
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
          task.taskSource === 'AI_TASK'   ? 'bg-purple-100 text-purple-700' :
          task.taskSource === 'WI_STEP'  ? 'bg-blue-100 text-blue-700'    :
          task.taskSource === 'DRAWING'  ? 'bg-teal-100 text-teal-700'    :
                                           'bg-gray-100 text-gray-600'
        }`}>
          {task.taskSource === 'AI_TASK' ? <Brain className="w-4 h-4" /> :
           task.taskSource === 'WI_STEP' ? <FileText className="w-4 h-4" /> :
           task.taskSource === 'DRAWING' ? <Eye className="w-4 h-4" /> : 'M'}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-semibold text-gray-900">{task.title}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.bg} ${meta.text}`}>
              {meta.label}
            </span>
            <GoNoBadge status={gng} />
            {task.holdPointLinkage && (
              <span className="text-xs bg-orange-100 text-orange-700 font-bold px-2 py-0.5 rounded-full">
                HOLD POINT
              </span>
            )}
            {task.taskSource === 'AI_TASK' && task.approvalStatus === 'DRAFT' && (
              <span className="text-xs bg-purple-100 text-purple-700 font-semibold px-2 py-0.5 rounded-full">
                AI — awaiting approval
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {task.jobNumber}
            {submission?.employeeName && ` · ${submission.employeeName}`}
            {submission?.startedAt && ` · Started ${new Date(submission.startedAt).toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit'})}`}
          </div>

          {/* Photo progress */}
          {task.photoCount > 0 && (
            <div className="flex items-center gap-2 mt-1.5">
              <Camera className="w-3.5 h-3.5 text-gray-400" />
              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-24">
                <div
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width:`${Math.min(((submission?.photosSubmitted??0)/task.photoCount)*100, 100)}%` }}
                />
              </div>
              <span className="text-xs text-gray-400">{submission?.photosSubmitted??0}/{task.photoCount} photos</span>
            </div>
          )}

          {/* Materials mini-list */}
          {expanded && task.materialsRequired.length > 0 && (
            <div className="mt-2 space-y-1">
              {task.materialsRequired.map((m, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    m.status === 'AVAILABLE_ON_SITE' ? 'bg-green-500' :
                    m.status === 'AVAILABLE_WAREHOUSE' ? 'bg-amber-400' :
                    m.status === 'ON_ORDER_ETA' ? 'bg-blue-400' : 'bg-red-400'
                  }`} />
                  <span className="text-gray-700">{m.description}</span>
                  <span className="text-gray-400 ml-auto">{m.quantity} {m.unit}</span>
                </div>
              ))}
            </div>
          )}

          {submission?.completionNote && (
            <p className="text-xs text-gray-500 italic mt-1 bg-gray-50 rounded px-2 py-1">
              "{submission.completionNote}"
            </p>
          )}
          {submission?.reworkNote && (
            <p className="text-xs text-red-700 mt-1 bg-red-50 rounded px-2 py-1 flex items-start gap-1">
              <Flag className="w-3 h-3 flex-shrink-0 mt-0.5" />
              {submission.reworkNote}
            </p>
          )}
        </div>

        {/* Actions + expand */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {task.materialsRequired.length > 0 && (
            <button onClick={() => setExpanded(v => !v)}
              className="p-1 rounded hover:bg-gray-100">
              {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
          )}
          {status === 'COMPLETE_PENDING_REVIEW' && submission && (
            <>
              <button onClick={() => onAccept(submission.id)}
                className="flex items-center gap-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2.5 py-1.5 rounded-lg font-medium">
                <CheckCircle className="w-3.5 h-3.5" />Accept
              </button>
              <button onClick={() => setShowRework(v => !v)}
                className="flex items-center gap-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 px-2.5 py-1.5 rounded-lg font-medium">
                <Flag className="w-3.5 h-3.5" />Rework
              </button>
            </>
          )}
        </div>
      </div>

      {/* Rework input */}
      {showRework && submission && (
        <div className="px-4 py-3 border-t bg-red-50 flex gap-2">
          <input
            className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
            placeholder="Describe what needs to be corrected…"
            value={reworkNote}
            onChange={e => setReworkNote(e.target.value)}
          />
          <button
            onClick={() => { onRework(submission.id, reworkNote); setShowRework(false); setReworkNote('') }}
            disabled={!reworkNote.trim()}
            className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-50">
            Send
          </button>
          <button onClick={() => setShowRework(false)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600">
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}

// ── Photo card ─────────────────────────────────────────────────────
function PhotoCard({ photo, currentUser, onAccept, onFlag }: {
  photo: MobilePhoto; currentUser: any
  onAccept: (id: string) => void
  onFlag:   (id: string) => void
}) {
  const rm = photo.reviewStatus === 'ACCEPTED'       ? 'border-green-200 bg-green-50/20'
           : photo.reviewStatus === 'FLAGGED_REWORK'  ? 'border-red-200 bg-red-50/20'
           : 'border-gray-200'

  return (
    <div className={`bg-white border rounded-xl overflow-hidden ${rm}`}>
      {/* Photo placeholder */}
      <div className="bg-gray-100 h-36 flex items-center justify-center relative">
        <div className="text-center">
          <Camera className="w-8 h-8 text-gray-300 mx-auto mb-1" />
          <div className="text-xs text-gray-400 font-medium">{photo.photoType} · Photo {(photo as any).photoIndex ?? ''}</div>
          <div className="text-xs text-gray-400">{photo.taskTitle}</div>
        </div>
        <div className="absolute top-2 left-2">
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${
            photo.reviewStatus === 'ACCEPTED'       ? 'bg-green-100 text-green-700'
            : photo.reviewStatus === 'FLAGGED_REWORK' ? 'bg-red-100 text-red-700'
            : 'bg-gray-100 text-gray-600'
          }`}>
            {photo.reviewStatus === 'PENDING'         ? 'Pending'
            : photo.reviewStatus === 'ACCEPTED'       ? 'Accepted'
            : 'Rework'}
          </span>
        </div>
      </div>
      <div className="p-3 space-y-1.5">
        <div className="text-xs font-medium text-gray-900">{photo.employeeName} · {photo.jobNumber}</div>
        {photo.gpsAddress && (
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <MapPin className="w-3 h-3" />{photo.gpsAddress}
          </div>
        )}
        <div className="text-xs text-gray-400">
          {new Date(photo.timestamp).toLocaleString('en-AU', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
        </div>
        {photo.note && <p className="text-xs text-gray-600 italic">"{photo.note}"</p>}
        {photo.reworkNote && <p className="text-xs text-red-700 bg-red-50 rounded px-2 py-1">{photo.reworkNote}</p>}
        {photo.reviewStatus === 'PENDING' && (
          <div className="flex gap-2 pt-1">
            <button onClick={() => onAccept(photo.id)}
              className="flex-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 py-1.5 rounded-lg font-medium flex items-center justify-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" />Accept
            </button>
            <button onClick={() => onFlag(photo.id)}
              className="flex-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 py-1.5 rounded-lg font-medium flex items-center justify-center gap-1">
              <Flag className="w-3.5 h-3.5" />Flag
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Deliveries (GRN) form ──────────────────────────────────────────
function DeliveriesView({ scopedJobIds }: { scopedJobIds: string[] | null }) {
  const [form, setForm] = useState({
    jobId:       '',
    poRef:       'PO-0051',
    docketNo:    '',
    qty:         '',
    condition:   'GOOD' as 'GOOD' | 'DAMAGED' | 'PARTIAL',
    note:        '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [error,     setError]     = useState('')

  const availJobs = MOCK_JOBS.filter(j =>
    !['COMPLETED','CLOSED'].includes(j.status) &&
    (scopedJobIds === null || scopedJobIds.includes(j.id))
  )

  const handleSubmit = async () => {
    if (!form.jobId || !form.poRef || !form.qty) {
      setError('Job, PO, and quantity are required.')
      return
    }
    setError('')
    const job = MOCK_JOBS.find(j => j.id === form.jobId)
    try {
      addDeliveryConfirmation({
        id:               `dc_${Date.now()}`,
        poRef:            form.poRef,
        jobId:            form.jobId,
        jobNumber:        job?.jobNumber ?? '',
        supplierId:       'field-delivery',
        supplierName:     'Site delivery (field confirmation)',
        confirmedAt:      new Date().toISOString(),
        confirmedByName:  'Field crew',
        deliveryDocketRef:form.docketNo || 'FIELD-DEL',
        allReceived:      form.condition === 'GOOD',
        hasDiscrepancy:   form.condition !== 'GOOD',
        notes:            form.note || '',
        lines: [{
          bomLineId:        null,
          status:           (form.condition === 'GOOD' ? 'DELIVERED' : form.condition) as any,
          condition:        form.condition as 'GOOD'|'DAMAGED'|'PARTIAL'|'REJECTED',
          deliveryLocation: 'ON_SITE',
        } as any],
      })
      setSubmitted(true)
      setTimeout(() => {
        setSubmitted(false)
        setForm(f => ({ ...f, docketNo:'', qty:'', note:'' }))
      }, 3000)
    } catch (e) {
      setError('Failed to record delivery. Please try again.')
    }
  }

  if (submitted) return (
    <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
      <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
      <p className="text-green-800 font-bold text-base">Delivery confirmed!</p>
      <p className="text-green-600 text-sm mt-1">GRN recorded. BOM stock status updated to ON_SITE.</p>
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 text-sm text-teal-800">
        <p className="font-semibold">Site-direct delivery confirmation</p>
        <p className="text-xs mt-0.5">
          Confirm materials received directly on site. This creates a GRN record and updates the BOM stock status to ON_SITE — unlocking Go/No-Go for linked tasks.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />{error}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Job <span className="text-red-500">*</span></label>
            <select value={form.jobId} onChange={e => setForm(f=>({...f,jobId:e.target.value}))}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm">
              <option value="">Select job…</option>
              {availJobs.map(j => (
                <option key={j.id} value={j.id}>{j.jobNumber}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Purchase order <span className="text-red-500">*</span></label>
            <select value={form.poRef} onChange={e => setForm(f=>({...f,poRef:e.target.value}))}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm">
              <option value="PO-0051">PO-0051 — Signal poles & heads</option>
              <option value="PO-0049">PO-0049 — SCATS controller</option>
              <option value="PO-0042">PO-0042 — Cable</option>
              <option value="PO-0044">PO-0044 — Conduit & cable pits</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Delivery docket no.</label>
            <input value={form.docketNo} onChange={e => setForm(f=>({...f,docketNo:e.target.value}))}
              placeholder="e.g. DN-2026-44821"
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Qty received <span className="text-red-500">*</span></label>
            <input type="number" min="1" value={form.qty} onChange={e => setForm(f=>({...f,qty:e.target.value}))}
              placeholder="0"
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Condition</label>
            <select value={form.condition} onChange={e => setForm(f=>({...f,condition:e.target.value as any}))}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm">
              <option value="GOOD">Good — all items as ordered</option>
              <option value="DAMAGED">Damaged — items received damaged</option>
              <option value="PARTIAL">Short delivery — fewer items than expected</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
            <input value={form.note} onChange={e => setForm(f=>({...f,note:e.target.value}))}
              placeholder="Shortages, damage details…"
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="flex justify-end">
          <button onClick={handleSubmit}
            className="px-5 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-bold hover:bg-teal-700 flex items-center gap-2">
            <Truck className="w-4 h-4" />Confirm receipt — create GRN
          </button>
        </div>
      </div>
    </div>
  )
}

// ── AI Task Approval Panel ─────────────────────────────────────────
function AIApprovalPanel({
  tasks, currentUser, onRefresh,
}: {
  tasks:       AssignableTask[]
  currentUser: any
  onRefresh:   () => void
}) {
  const draftAITasks = tasks.filter(t => t.taskSource === 'AI_TASK' && t.approvalStatus === 'DRAFT')
  if (draftAITasks.length === 0) return null

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Brain className="w-4 h-4 text-purple-600" />
        <span className="text-sm font-semibold text-purple-800">
          {draftAITasks.length} AI-suggested task{draftAITasks.length !== 1 ? 's' : ''} awaiting PM approval
        </span>
      </div>
      {draftAITasks.map(t => (
        <div key={t.taskId} className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Brain className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">{t.title}</p>
              {t.aiSourceDoc && (
                <p className="text-xs text-purple-600 mt-0.5">Source: {t.aiSourceDoc}</p>
              )}
              <p className="text-xs text-gray-600 mt-1">{t.description}</p>
              {(t as any).aiRationale && (
                <p className="text-xs text-purple-700 italic mt-1 bg-purple-100 rounded px-2 py-1">
                  AI rationale: {(t as any).aiRationale}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={async () => { await approveAITask(t.taskId, currentUser?.id??'u_pm', currentUser?.fullName??'PM'); onRefresh() }}
              className="flex-1 py-1.5 rounded-lg bg-green-100 text-green-700 text-xs font-semibold hover:bg-green-200 flex items-center justify-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" />Approve & push to crew
            </button>
            <button
              onClick={async () => { await rejectAITask(t.taskId); onRefresh() }}
              className="flex-1 py-1.5 rounded-lg bg-red-100 text-red-700 text-xs font-semibold hover:bg-red-200 flex items-center justify-center gap-1">
              <X className="w-3.5 h-3.5" />Reject
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Main TasksPhotosTab ────────────────────────────────────────────
export function TasksPhotosTab({ scopedJobIds, currentUser }: {
  scopedJobIds: string[] | null
  currentUser:  any
}) {
  const [tasks,       setTasks]     = useState<AssignableTask[]>([])
  const [submissions, setSubs]      = useState<MobileTaskRecord[]>([])
  const [photos,      setPhotos]    = useState<MobilePhoto[]>([])
  const [view,        setView]      = useState<'tasks' | 'photos' | 'deliveries'>('tasks')
  const [jobFilter,   setJobFilter] = useState('ALL')
  const [loading,     setLoading]   = useState(true)

  const availJobs = MOCK_JOBS.filter(j =>
    !['COMPLETED','CLOSED'].includes(j.status) &&
    (scopedJobIds === null || scopedJobIds.includes(j.id))
  )

  const load = useCallback(async () => {
    const [t, s, p] = await Promise.all([
      getAllAssignableTasks(jobFilter !== 'ALL' ? jobFilter : undefined),
      getTaskSubmissions(jobFilter !== 'ALL' ? jobFilter : undefined),
      getPhotoSubmissions(jobFilter !== 'ALL' ? jobFilter : undefined),
    ])
    setTasks(t)
    setSubs(s)
    setPhotos(p)
    setLoading(false)
  }, [jobFilter])

  useEffect(() => { load() }, [load])

  const pendingReview = submissions.filter(s => s.status === 'COMPLETE_PENDING_REVIEW').length
  const pendingPhotos = photos.filter(p => p.reviewStatus === 'PENDING').length

  async function handleAcceptTask(submissionId: string) {
    setSubs(prev => prev.map(s => s.id === submissionId
      ? { ...s, status: 'ACCEPTED', reviewedBy: currentUser?.fullName, reviewedAt: new Date().toISOString() }
      : s
    ))
  }

  async function handleReworkTask(submissionId: string, note: string) {
    setSubs(prev => prev.map(s => s.id === submissionId
      ? { ...s, status: 'REQUIRES_REWORK', reworkNote: note, reviewedBy: currentUser?.fullName, reviewedAt: new Date().toISOString() }
      : s
    ))
  }

  async function handleAcceptPhoto(photoId: string) {
    await approvePhoto(photoId, currentUser?.fullName ?? 'PM')
    setPhotos(prev => prev.map(p => p.id === photoId
      ? { ...p, reviewStatus: 'ACCEPTED', reviewedBy: currentUser?.fullName, reviewedAt: new Date().toISOString() }
      : p
    ))
  }

  async function handleFlagPhoto(photoId: string) {
    await rejectPhoto(photoId, 'Resubmit required — see task rework note.', currentUser?.fullName ?? 'PM', '', '', '')
    setPhotos(prev => prev.map(p => p.id === photoId
      ? { ...p, reviewStatus: 'FLAGGED_REWORK', reworkNote: 'Resubmit required.' }
      : p
    ))
  }

  if (loading) return (
    <div className="py-16 flex items-center justify-center text-gray-400">
      <RefreshCw className="w-5 h-5 animate-spin mr-2" />Loading tasks...
    </div>
  )

  return (
    <div className="space-y-4">
      {/* View toggle + job filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          {([
            { v:'tasks',      label:`Tasks (${pendingReview} pending)`      },
            { v:'photos',     label:`Photos (${pendingPhotos} to review)`   },
            { v:'deliveries', label:'Deliveries / GRN'                      },
          ] as const).map(t => (
            <button key={t.v} onClick={() => setView(t.v)}
              className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
                view === t.v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Job filter */}
        {availJobs.length > 1 && (
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setJobFilter('ALL')}
              className={`text-xs px-3 py-1.5 rounded-full font-medium ${
                jobFilter === 'ALL' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              All jobs
            </button>
            {availJobs.map(j => (
              <button key={j.id} onClick={() => setJobFilter(j.id)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium ${
                  jobFilter === j.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                {j.jobNumber}
              </button>
            ))}
          </div>
        )}

        {view === 'photos' && (
          <button className="ml-auto text-sm text-gray-600 border border-gray-200 rounded-xl px-3 py-1.5 hover:bg-gray-50 flex items-center gap-1.5">
            <Download className="w-4 h-4" />Export to handover
          </button>
        )}
      </div>

      {/* ── Tasks view ─────────────────────────────────────────── */}
      {view === 'tasks' && (
        <div className="space-y-4">
          {/* AI approval panel */}
          <AIApprovalPanel tasks={tasks} currentUser={currentUser} onRefresh={load} />

          {/* Task cards */}
          <div className="space-y-3">
            {tasks
              .filter(t => !(t.taskSource === 'AI_TASK' && t.approvalStatus === 'DRAFT'))
              .map(t => {
                const sub = submissions.find(s => s.taskId === t.taskId) ?? null
                return (
                  <TaskCard
                    key={t.taskId}
                    task={t}
                    submission={sub}
                    currentUser={currentUser}
                    onAccept={handleAcceptTask}
                    onRework={handleReworkTask}
                  />
                )
              })}
          </div>

          {tasks.filter(t => !(t.taskSource === 'AI_TASK' && t.approvalStatus === 'DRAFT')).length === 0 && (
            <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
              <FileText className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No tasks configured</p>
              <p className="text-gray-300 text-sm mt-1">Configure tasks to appear on crew mobile devices</p>
            </div>
          )}
        </div>
      )}

      {/* ── Photos view ────────────────────────────────────────── */}
      {view === 'photos' && (
        <div>
          {pendingPhotos > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 mb-4">
              <strong>{pendingPhotos} photo{pendingPhotos !== 1 ? 's' : ''}</strong> awaiting PM review.
            </div>
          )}
          {photos.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
              <Camera className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No photo submissions</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {photos.map(ph => (
                <PhotoCard
                  key={ph.id}
                  photo={ph}
                  currentUser={currentUser}
                  onAccept={handleAcceptPhoto}
                  onFlag={handleFlagPhoto}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Deliveries view ────────────────────────────────────── */}
      {view === 'deliveries' && (
        <DeliveriesView scopedJobIds={scopedJobIds} />
      )}
    </div>
  )
}
